// src/oura/overview/hubStage.ts
//
// Where a business stands in the joined journey (JOURNEY-SPEC v1.1 §2.1,
// §2.2), worked out once for the three places that act on it: the business
// page header (at most two stage pills), the Overview Next card, and the one
// row action on the Businesses list. The three never disagree because they all
// read `hubStage()` and the verbs below.
//
// Onboarding stages defer to the onboarding module's own `nextAction()`, so the
// Overview Next card and the Onboarding page say the same words.
//
// Pure: no fetching, no React. `useHubStage` reads the facts.

import type { AeoAssessmentStatus, AeoBusinessDetail, AeoScoreHistoryEntry } from "@service/aeo";
import type { Onboarding } from "@service/aeo/onboarding";
import type { ScanDraftListItem, ScanDraftStatus } from "@service/aeo/scanDraftTypes";
import { draftPurpose } from "@service/aeo/scanDraftTypes";
import { firstCheckHref as sharedFirstCheckHref } from "../newscan/firstCheckLinks";
import { dayMonth, FIRST_CHECK_MINUTES, firstCheckOpensAt, nextAction, PRESS_NEXT, PRESS_PARAM, type NextAction, type OnboardingPlanFacts, type PlanRead } from "../onboarding/derive";
import { OURA_ROOT, ouraHref } from "../nav";
import { releaseLine } from "../onboarding/drafts";
import type { Tone } from "@oura/ui";
import { SIGN_PRESS } from "../newscan/flags";

/** An open draft of this business's 50, as the stage needs it. */
export interface HubDraft {
    id: string;
    kind: ScanDraftListItem["kind"];
    purpose: ScanDraftListItem["purpose"];
    status: ScanDraftStatus;
    createdAt: string | null;
    /** Blocker and decide cards still open. Null when not known (a list row). */
    open: number | null;
}

/** What the business row and its detail say, before onboarding and drafts are read. */
export interface HubFacts {
    businessId: string;
    name: string;
    domain: string;
    /** `aeo_businesses.state`. Undefined: not known. */
    state?: "prospect" | "client" | "churned";
    /** The engagement's status. Null: none sold. Undefined: not known. */
    engagementStatus?: "draft" | "signed" | "active" | "ended" | "cancelled" | null;
    /** Newest published timeline version. Null: never published. Undefined: not known. */
    timelinePublished?: number | null;
    /** A finished, usable scan exists. */
    scanned: boolean;
    /** When that scan was captured. */
    scannedAt: string | null;
    /** A scan running or failed after the last usable one. */
    run: HubRun | null;
    /** The next monthly check. Null: monthly checks are off for this business. */
    nextScanAt: string | null;
    /** The row these facts come from is still being read: no stage yet, so no pill changes under the pointer. */
    unread?: boolean;
    /**
     * The pitch was a New scan draft in Oura (the prefill read names its buy
     * mode): its 50 carry into the first AI check. False: measured outside
     * Oura, as the first check's Review says. Undefined or null: not known yet.
     */
    pitchInOura?: boolean | null;
}

export type HubRun =
    | { status: "running"; percent: number | null }
    | { status: "failed"; message: string };

export type HubStage =
    | { kind: "prospect_new" }
    | { kind: "prospect_draft"; draft: HubDraft }
    /** `trashable`: false for a signed client whose first check is running or failed, which is never deleted from the list. */
    | { kind: "prospect_running"; percent: number | null; trashable: boolean }
    /**
     * `draft`: an open draft of its 50, the place Try again picks up from. `retry`: "rescan" when a
     * usable scan exists and a newer run failed (factsFromDetail's failed `latest_attempt`), so Try
     * again re-runs the same 50 on the Rescan sheet; "draft" when there is no usable scan to re-run.
     */
    | { kind: "prospect_failed"; message: string; draft: HubDraft | null; trashable: boolean; retry: "draft" | "rescan" }
    | { kind: "prospect_scanned"; scannedAt: string | null; draft: HubDraft | null; pitchInOura: boolean | null }
    /** Signed and being set up. `checked`: the first AI check finished. */
    /** `planRead`: whether its plan facts are read (absent: taken as read); unknown facts claim nothing. */
    | { kind: "onboarding"; onboarding: Onboarding; checked: boolean; draft: HubDraft | null; plan: OnboardingPlanFacts | null; planRead?: PlanRead }
    /** A plan with no onboarding record and no scan (JOURNEY-SPEC §1.1 row Q). */
    | { kind: "active_unscanned" }
    /** `onPlan`: a plan is signed (an onboarding record, or the engagement says so); else a client with no plan in Oura. */
    /**
     * `publish`: true, the schedule is not published and is ours to publish; false, it is published;
     * null, not known yet (the row carries no timeline version and the plan facts do not say), so
     * nothing claims either way (Jack, #4284).
     */
    | { kind: "active"; publish: boolean | null; nextScanAt: string | null; draft: HubDraft | null; baselineAt: string | null; onPlan: boolean }
    | { kind: "ended"; draft: HubDraft | null };

/** Where a press goes. The screen that shows the press carries it out. */
export type HubAct =
    /** The New scan flow for this site, at Business (website known). */
    | { kind: "draft_new" }
    /** A first_check draft for a signed client, at Review, or at Start once approved. */
    | { kind: "first_check"; draftId: string | null; atStart: boolean }
    /** An open new_scan or edit draft, where it was left. */
    | { kind: "continue"; draft: HubDraft }
    /** The Client signed sheet, with this business fixed. */
    | { kind: "client_signed" }
    /** The Rescan sheet: the same 50 again. */
    | { kind: "rescan" }
    /** Edit the questions: a reviewed edit_set draft. */
    | { kind: "edit" }
    /**
     * The onboarding page. `press`: the Next card's own press runs there once on
     * arrival (a drawer, the payment date). False: the page only opens.
     */
    | { kind: "onboarding"; press: boolean }
    /** Take Q{n} as the signature question: the same write the Onboarding card's Use Q{n} makes. */
    | { kind: "signature"; n: number }
    /** The plan's Contract, where any of the 50 can be chosen. */
    | { kind: "contract" }
    | { kind: "timeline" };

/**
 * Hub presses that start a write; the rest open or resume content. One set for
 * every place a hub press renders, so maintenance disables the same ones (#3561).
 */
export const HUB_ACT_WRITES: ReadonlySet<HubAct["kind"]> = new Set<HubAct["kind"]>(["draft_new", "first_check", "client_signed", "rescan", "signature"]);

/** What decides the presses beside the stage: the New scan flow, and whether a client can be signed here. */
export interface HubOptions {
    draftFlow: boolean;
    /** Onboarding is on and its routes can create a client (else no Client signed press anywhere). */
    signOn: boolean;
}

export interface HubNext {
    tag: { label: string; tone: Tone } | null;
    title: string;
    why: string;
    /** The button. Null: nothing for us to press. */
    cta: string | null;
    act: HubAct | null;
    /** A quieter text press beside the button ("Choose another"). */
    secondary: { label: string; act: HubAct } | null;
    primary: boolean;
    late: boolean;
    /** 0 to 100 while a scan runs: the violet bar along the card's foot. */
    percent: number | null;
    /** The onboarding module's own answer, when this card is an onboarding one. */
    onboarding: NextAction | null;
    /** Who the release goes to, as the onboarding card says it (F10). Empty: no line. */
    recipients?: string;
}

export interface HubPill {
    label: string;
    act: HubAct;
    /** One blue pill per header, and only for a press that is ours to make next. */
    primary: boolean;
}

/** An open, not yet started draft of the business's own 50 (not a first check). */
export function isOpenDraft(d: Pick<HubDraft, "status">): boolean {
    return d.status === "building" || d.status === "ready" || d.status === "approved" || d.status === "failed";
}

/** A list item as the stage reads it. The list does not count open cards. */
export function hubDraftOf(d: ScanDraftListItem): HubDraft {
    return { id: d.id, kind: d.kind, purpose: d.purpose ?? null, status: d.status, createdAt: d.createdAt || null, open: null };
}

const FIRST_CHECK_DRAFT = (d: HubDraft | null) => !!d && draftPurpose(d) === "first_check";

/**
 * Where the business stands. `onboarding` undefined means its read has not
 * come back: a business is then read from its row alone, which never claims an
 * onboarding stage it does not know.
 */
export function hubStage(
    f: HubFacts,
    onboarding: Onboarding | null | undefined,
    draft: HubDraft | null,
    /** The plan facts the Onboarding page reads (signature, published), so both cards agree. */
    plan: OnboardingPlanFacts | null = null,
    /** Whether those facts are read. Absent: taken as read. Loading or failed: no claim from them. */
    planRead?: PlanRead,
): HubStage {
    // The business's own draft, not a signed client's first check (that one is the onboarding record's).
    const own = draft && !FIRST_CHECK_DRAFT(draft) && isOpenDraft(draft) ? draft : null;
    const ended = f.state === "churned" || f.engagementStatus === "ended" || f.engagementStatus === "cancelled";
    if (ended) return { kind: "ended", draft: own };
    // Still the onboarding page's to say while its Next card has anything left (publish comes last there).
    if (onboarding && (!onboarding.completedAt || nextAction(onboarding, Date.now(), { plan, planRead }).kind !== "done")) {
        return { kind: "onboarding", onboarding, checked: onboarding.scan.status === "complete", draft: own, plan, ...(planRead ? { planRead } : {}) };
    }
    // A client has an onboarding record or a signed engagement, as the backend's list stage reads it
    // (`list_stage`, #6091). `state === "client"` alone is not one: Create merchant login sets it with no
    // contract and no onboarding, and reading that as a client dead-ended its Onboarding tab (#4517).
    const client = !!onboarding || f.engagementStatus === "signed" || f.engagementStatus === "active";
    if (client) {
        if (!f.scanned && !onboarding) {
            // Its first check already started: show the run, never "Draft the 50" again (Jack, #4289).
            if (f.run?.status === "running") return { kind: "prospect_running", percent: f.run.percent, trashable: false };
            if (f.run?.status === "failed") return { kind: "prospect_failed", message: f.run.message, draft: own, trashable: false, retry: "draft" };
            return { kind: "active_unscanned" };
        }
        // The plan facts know a publish the business row has not caught up with yet. Only a real
        // version counts: undefined is "not known", never "published" (Jack, #4284, #4287). Known
        // from the row (a version or null) or from the plan facts; neither: `publish` is null.
        const published = typeof f.timelinePublished === "number" || plan?.published === true;
        const publicationKnown = f.timelinePublished !== undefined || plan !== null;
        const baselineAt = onboarding?.scan.status === "complete" ? onboarding.scan.finishedAt : f.scannedAt;
        const onPlan = !!onboarding || f.engagementStatus === "signed" || f.engagementStatus === "active";
        const publish = published ? false : publicationKnown ? true : null;
        return { kind: "active", publish, nextScanAt: f.nextScanAt, draft: own, baselineAt, onPlan };
    }
    if (f.run?.status === "running") return { kind: "prospect_running", percent: f.run.percent, trashable: true };
    if (f.run?.status === "failed") {
        return { kind: "prospect_failed", message: f.run.message, draft: own, trashable: true, retry: f.scanned ? "rescan" : "draft" };
    }
    if (!f.scanned) return own ? { kind: "prospect_draft", draft: own } : { kind: "prospect_new" };
    return { kind: "prospect_scanned", scannedAt: f.scannedAt, draft: own, pitchInOura: f.pitchInOura ?? null };
}

/** "Continue the draft" while a draft of these 50 is open, else "Edit the questions". */
function editPill(draft: HubDraft | null): HubPill {
    return draft ? { label: "Continue the draft", act: { kind: "continue", draft }, primary: false } : { label: "Edit the questions", act: { kind: "edit" }, primary: false };
}

/** "Continue the draft" while a draft is open, else "Edit and rescan": the Review page, then a run. */
function editRescanPill(draft: HubDraft | null): HubPill {
    return draft ? { label: "Continue the draft", act: { kind: "continue", draft }, primary: false } : { label: "Edit and rescan", act: { kind: "edit" }, primary: false };
}

/**
 * The first AI check's results are in and not sent yet, on a record that tracks approval: the
 * answers page shows FirstResultsBanner, and Edit and rescan sits there beside Approve results
 * instead of in the header (Jovi, 20 Oct).
 */
export function firstResultsAwaitSend(o: Pick<Onboarding, "scan" | "resultsSentAt" | "resultsApprovedAt"> | null | undefined): boolean {
    return Boolean(o && o.scan.status === "complete" && o.resultsSentAt === null && o.resultsApprovedAt !== undefined);
}

/** The pill that opens the 50 to edit and run again ("Edit and rescan" / "Continue the draft" / "Edit the questions"). */
export function isEditRescanPill(p: HubPill): boolean {
    return p.act.kind === "first_check" || p.act.kind === "continue" || p.act.kind === "edit";
}

/** The header's stage pills, at most two (JOURNEY-SPEC §2.2). Share report is not one of them. */
export function headerPills(stage: HubStage, opts: Pick<HubOptions, "signOn">): HubPill[] {
    switch (stage.kind) {
        case "prospect_new":
            return [{ label: "Draft the 50", act: { kind: "draft_new" }, primary: true }];
        case "prospect_draft":
            return [{ label: "Continue the draft", act: { kind: "continue", draft: stage.draft }, primary: true }];
        case "prospect_running":
        case "prospect_failed":
            return [];
        case "prospect_scanned":
            // No Client signed where onboarding cannot create the client: its routes are not live there.
            return opts.signOn ? [{ label: SIGN_PRESS, act: { kind: "client_signed" }, primary: true }, editPill(stage.draft)] : [editPill(stage.draft)];
        // Rescan and Edit the questions are one button (Jack, 20 Oct 2026): both
        // go through the Review page, where staff confirm the 50 and run again.
        // While onboarding too, once the first check has run: the server refuses a second first-check
        // draft (409 first_check_done, "Edit the questions instead"), so this opens an edit_set draft (#4461).
        case "onboarding":
            return stage.checked ? [editRescanPill(stage.draft)] : [];
        case "active_unscanned":
            return [{ label: "Draft the 50", act: { kind: "first_check", draftId: null, atStart: false }, primary: true }];
        case "active":
        case "ended":
            return [editRescanPill(stage.draft)];
    }
}

const NEEDS_US = { label: "Needs us", tone: "blue" as Tone };
const OURA_WORKING = { label: "Oura is working", tone: "violet" as Tone };
const MINUTES_IN_ALL = `About ${FIRST_CHECK_MINUTES} minutes in all.`;

/** "22 of 50 questions need your call."; nothing when the count is not known. Onboarding's own words. */
function toDecide(open: number | null): string {
    return open === null ? "" : open === 0 ? " Nothing to decide." : ` ${open} of 50 questions ${open === 1 ? "needs" : "need"} your call.`;
}

/** The onboarding kinds whose press is the first check's draft (F9 names some of them). */
const FIRST_CHECK_KINDS: ReadonlySet<string> = new Set(["review_draft", "start_scan", "retry_scan", "draft_failed", "retry_draft"]);

/** Where an onboarding Next card's button goes, with the New scan flow on: what the Onboarding card's own press does. */
function onboardingAct(o: Onboarding, next: NextAction, draftFlow: boolean, plan: OnboardingPlanFacts | null): HubAct | null {
    if (!next.cta) return null;
    const kind: string = next.kind;
    if (kind === "publish") return { kind: "timeline" };
    // Use Q14 makes the write here too; with no question to offer, the Contract is where one is chosen.
    if (kind === "choose_signature") return plan?.closest ? { kind: "signature", n: plan.closest.n } : { kind: "contract" };
    if (draftFlow && FIRST_CHECK_KINDS.has(kind) && o.buyMode !== "online") {
        // The same step the Onboarding page opens: Start once nothing is left to review.
        return { kind: "first_check", draftId: o.scan.draft?.id ?? null, atStart: firstCheckOpensAt(o) === "start" };
    }
    return { kind: "onboarding", press: true };
}

/** The tag the onboarding page gives the same state. */
function onboardingTag(next: NextAction): HubNext["tag"] {
    if (next.kind === "done") return null;
    if (next.tag) return next.tag;
    if (next.waitingOn) return { label: `Needs ${next.waitingOn}`, tone: "amber" };
    if (next.who === "merchant") return { label: "Waiting on client", tone: "amber" };
    if (next.who === "oura") return OURA_WORKING;
    return NEEDS_US;
}

/**
 * The Overview Next card: the onboarding card's own words while a client is
 * being set up, else the stage's state from JOURNEY-SPEC §2.2. Null hides it
 * (an ended plan).
 */
export function hubNext(stage: HubStage, name: string, opts: HubOptions, now = Date.now()): HubNext | null {
    const base = { primary: true, late: false, percent: null, onboarding: null, secondary: null } as const;
    switch (stage.kind) {
        case "prospect_new":
            return { ...base, tag: NEEDS_US, title: `Scan ${name}`, why: "No scan yet. Drafting the 50 is free.", cta: "Draft the 50", act: { kind: "draft_new" } };
        case "prospect_draft": {
            const drafted = dayMonth(stage.draft.createdAt);
            return {
                ...base,
                tag: NEEDS_US,
                title: `Finish the 50 for ${name}`,
                why: `${drafted ? `Drafted ${drafted}.` : "Drafted."}${toDecide(stage.draft.open)} Nothing paid yet.`,
                cta: "Continue the draft",
                act: { kind: "continue", draft: stage.draft },
            };
        }
        case "prospect_running":
            return {
                ...base,
                tag: OURA_WORKING,
                title: `${name}'s scan is running`,
                why: stage.percent !== null ? `${stage.percent}% done. ${MINUTES_IN_ALL}` : MINUTES_IN_ALL,
                cta: null,
                act: null,
                primary: false,
                percent: stage.percent ?? 0,
            };
        case "prospect_failed":
            // A failed rescan of a business with a usable scan re-runs the same 50 on the Rescan sheet
            // (Jack, #4285). With no usable scan, back into the draft flow: the Rescan route re-asks the
            // last scan's inputs and refuses a business without one. An open draft picks up where it was.
            return {
                ...base, tag: NEEDS_US, title: `${name}'s scan failed`, why: stage.message, cta: "Try again",
                act: stage.retry === "rescan"
                    ? { kind: "rescan" }
                    : stage.draft ? { kind: "continue", draft: stage.draft } : { kind: "draft_new" },
                late: true,
            };
        case "prospect_scanned": {
            const scanned = dayMonth(stage.scannedAt);
            return {
                ...base,
                tag: { label: `Waiting on ${name}`, tone: "gray" },
                title: `When ${name} signs`,
                // The rule the first check's Review uses: only a pitch drafted in Oura carries its 50.
                why: `${scanned ? `Scanned ${scanned}. ` : ""}${
                    stage.pitchInOura === true
                        ? "The pitched 50 carry into the first AI check."
                        : stage.pitchInOura === false
                          ? "Measured outside Oura. The first AI check is drafted from the site and the client's answers."
                          : ""
                }`.trim(),
                cta: opts.signOn ? SIGN_PRESS : null,
                act: opts.signOn ? { kind: "client_signed" } : null,
                primary: false,
            };
        }
        case "onboarding": {
            const next = nextAction(stage.onboarding, now, { plan: stage.plan, planRead: stage.planRead });
            const o = stage.onboarding;
            return {
                tag: onboardingTag(next),
                title: next.title,
                why: next.why,
                cta: next.cta,
                act: onboardingAct(o, next, opts.draftFlow, stage.plan),
                secondary: next.kind === "choose_signature" && next.secondary ? { label: next.secondary, act: { kind: "contract" } } : null,
                primary: next.primary,
                late: next.late,
                percent: o.scan.status === "running" ? o.scan.percent ?? 0 : null,
                onboarding: next,
                recipients: next.kind === "release" ? releaseLine(o, next) : "",
            };
        }
        case "active_unscanned":
            return {
                ...base,
                tag: NEEDS_US,
                title: `Run ${name}'s first AI check`,
                why: "Set up before onboarding. No AI check yet.",
                cta: "Draft the 50",
                act: { kind: "first_check", draftId: null, atStart: false },
            };
        case "active":
            if (stage.publish === null) {
                // Not known yet: neither "publish it" nor "published". The timeline says which.
                return {
                    ...base, tag: { label: "Not known yet", tone: "gray" }, title: `${name}'s schedule`,
                    why: "Whether the schedule is published is not known yet. The timeline shows it.",
                    cta: "Open the timeline", act: { kind: "timeline" }, primary: false,
                };
            }
            if (stage.publish) {
                return { ...base, tag: NEEDS_US, title: `Publish ${name}'s schedule`, why: `${name} sees no dates until it is published.`, cta: "Open the timeline", act: { kind: "timeline" } };
            }
            if (!stage.nextScanAt) {
                // No plan in Oura yet: nothing for this card to say (What to do next says where to add it).
                if (!stage.onPlan) return null;
                // Nothing due and no monthly check: say where the client stands rather than show nothing.
                const baseline = dayMonth(stage.baselineAt);
                return {
                    ...base, tag: null, title: `${name} is on plan`,
                    why: `${baseline ? `Baseline ${baseline}. ` : ""}Monthly checks are off. Rescan asks the same 50 when you want a new reading.`,
                    cta: null, act: null, primary: false,
                };
            }
            return { ...base, tag: OURA_WORKING, title: `Next monthly check ${dayMonth(stage.nextScanAt)}`, why: "Asks the same 50.", cta: null, act: null, primary: false };
        case "ended":
            return null;
    }
}

/**
 * The list's one row action: the Next card's verb when the press is ours, else
 * nothing (JOURNEY-SPEC §2.1). Ended rows have none.
 */
export function rowAction(stage: HubStage, name: string, opts: HubOptions): { label: string; act: HubAct } | null {
    if (stage.kind === "ended") return null;
    if (stage.kind === "active") {
        if (stage.publish === null) return { label: "Check the schedule", act: { kind: "timeline" } };
        return stage.publish ? { label: "Publish", act: { kind: "timeline" } } : null;
    }
    const next = hubNext(stage, name, opts);
    if (!next?.cta || !next.act) return null;
    // Waiting on the client or on Oura: nothing to press from the list. A payment
    // waits on the client, yet recording it is ours (§2.1 "Waiting for payment").
    if (next.onboarding && next.onboarding.who !== "staff" && next.onboarding.kind !== "record_payment") return null;
    // The list names the act, not the screen it opens (§2.1). A signature is chosen on the
    // Onboarding page, beside the question it would be: the row never writes one blind.
    if (next.onboarding?.kind === "choose_signature") return { label: "Choose a question", act: { kind: "onboarding", press: false } };
    return { label: next.act.kind === "timeline" ? "Publish" : next.cta, act: next.act };
}

/**
 * Prospects, ended businesses and a client still being set up carry the trash icon (call 11).
 * A client in onboarding is a signed client that has not started its plan, so the list could
 * always remove it (the confirm names the business and says everything Oura holds is gone);
 * the stage must not take that away. An active client does not carry it here.
 */
export function canTrash(stage: HubStage): boolean {
    // A running or failed first check is a prospect's only when the business is not a signed client.
    if (stage.kind === "prospect_running" || stage.kind === "prospect_failed") return stage.trashable;
    return stage.kind.startsWith("prospect") || stage.kind === "onboarding" || stage.kind === "ended";
}

/* ---- the URLs a press opens ---- */

const enc = encodeURIComponent;

/** The New scan flow, prefilled with this site (the Website step reads `site`). */
export function draftNewHref(domain: string): string {
    return `${OURA_ROOT}/businesses/new?site=${enc(domain)}`;
}

/** A signed client's First AI check draft (§2.5): Review, or Start once approved. */
export function firstCheckHref(businessId: string, draftId: string | null, atStart: boolean): string {
    return sharedFirstCheckHref(businessId, { draftId, step: atStart ? "start" : null });
}

/** An open draft where it was left: a new_scan draft on the New scan route, an edit on the business's. */
export function continueHref(businessId: string, draft: HubDraft): string {
    if (draft.kind === "new_scan") return `${OURA_ROOT}/businesses/new?draft=${enc(draft.id)}`;
    return `${OURA_ROOT}/businesses/${enc(businessId)}/questions/draft?draft=${enc(draft.id)}`;
}

/**
 * Where a press goes, as a URL; null for a press carried out on the screen
 * itself (a sheet, the signature write). The list and the business page both
 * read this, so a press opens the same place from either.
 */
export function hubActHref(businessId: string, domain: string, a: HubAct): string | null {
    const staff = { role: "oura-staff" as const, businessId };
    switch (a.kind) {
        case "draft_new": return draftNewHref(domain);
        case "first_check": return firstCheckHref(businessId, a.draftId, a.atStart);
        case "continue": return continueHref(businessId, a.draft);
        case "edit": return `${OURA_ROOT}/businesses/${enc(businessId)}/questions/draft`;
        case "onboarding": return `${ouraHref("onboarding", staff)}${a.press ? `?${PRESS_PARAM}=${PRESS_NEXT}` : ""}`;
        case "contract": return ouraHref("contract", staff);
        // Straight to the publish card (it scrolls into view on #publish).
        case "timeline": return `${ouraHref("timeline", staff)}#publish`;
        case "client_signed":
        case "rescan":
        case "signature":
            return null;
    }
}

/** The facts a row of GET /businesses gives, as the stage reads them. */
export function factsFromRow(row: {
    id: string;
    domain: string;
    business_name: string;
    state?: HubFacts["state"];
    engagement_status?: HubFacts["engagementStatus"];
    timeline_published_version?: number | null;
    latest_assessment: { status: AeoAssessmentStatus; captured_at?: string | null; gated?: boolean; error_detail?: string | null } | null;
}, running: { percent: number | null } | null): HubFacts {
    const la = row.latest_assessment;
    const usable = !!la && (la.status === "complete" || la.status === "partial") && !la.gated;
    const failed = !!la && (la.status === "failed" || (la.status !== "complete" && la.status !== "partial" && la.gated === true));
    // The row's own status, before the job store has the run: a queued or running scan is
    // scanning, so no row offers a second scan meanwhile (Jack, #4284).
    const assessmentRunning = !!la && (la.status === "queued" || la.status === "running");
    return {
        businessId: row.id,
        name: row.business_name || row.domain,
        domain: row.domain,
        state: row.state,
        engagementStatus: row.engagement_status,
        timelinePublished: row.timeline_published_version,
        scanned: usable,
        scannedAt: usable ? la?.captured_at ?? null : null,
        run: running
            ? { status: "running", percent: running.percent }
            : assessmentRunning
              ? { status: "running", percent: null }
              : failed
                ? { status: "failed", message: la?.error_detail || "The scan stopped before it finished." }
                : null,
        // The list row does not carry it; the list never shows the monthly state.
        nextScanAt: null,
    };
}

/** The facts GET /businesses/{id} gives (the latest scan, never a past one opened from the picker). */
export function factsFromDetail(d: AeoBusinessDetail): HubFacts {
    const la = d.latest_assessment;
    const running = la && (la.status === "queued" || la.status === "running") ? { percent: null } : null;
    const facts = factsFromRow(
        { ...d, latest_assessment: la ? { ...la, captured_at: la.captured_at ?? la.completed_at ?? la.created_at ?? null } : null },
        running,
    );
    // A newer run that failed after the reported one (a rescan that did not replace it).
    const attempt = d.latest_attempt;
    const failedAfter = attempt && attempt.status === "failed" && !running
        ? { status: "failed" as const, message: attempt.error_detail || "The scan stopped before it finished." }
        : null;
    return { ...facts, run: facts.run ?? failedAfter, nextScanAt: d.next_auto_scan_at ?? null };
}

/**
 * What the Rescan sheet names: the set the last scan asked, counted as the
 * question history counts it (a new version each time the set's text
 * changes), and what the last scan cost. Null where the history does not say.
 */
export function rescanInfoFrom(history: AeoScoreHistoryEntry[]): { setVersion: number | null; costUsd: number | null } {
    const runs = history.filter((h) => h.status !== "failed" && h.status !== "cancelled");
    let version = 0;
    let last: string | null = null;
    let known = false;
    for (const h of [...runs].reverse()) {
        const id = h.visibility?.questionSetId ?? null;
        if (!id) continue;
        known = true;
        if (id !== last) version += 1;
        last = id;
    }
    const latest = runs[0];
    const cost = latest?.cost?.actual_usd ?? latest?.cost?.estimated_usd ?? null;
    return { setVersion: known ? version : null, costUsd: typeof cost === "number" && cost > 0 ? cost : null };
}
